<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Modal from '../../../../components/Modal.vue';
import LabelStageMappings from './LabelStageMappings.vue';
import PipelineAutoAssignmentSetting from './PipelineAutoAssignmentSetting.vue';
import PipelineLossReasonSetting from './PipelineLossReasonSetting.vue';
import { usePipelinePermissions } from '../composables/usePipelinePermissions';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import ConfirmModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
import logger from 'shared/helpers/logger';

const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
});
const emit = defineEmits(['close']);
const { t } = useI18n();
const store = useStore();
const { isAdmin } = usePipelinePermissions();
const { showAlert } = useAlert();
const currentAccountId = computed(
  () =>
    store.getters.getCurrentAccountId ??
    store.getters.getCurrentAccount?.id ??
    null
);
let accountGeneration = 0;
let saveGeneration = 0;
const normalizedAccountId = () => {
  const accountId = currentAccountId.value;
  if (accountId === undefined || accountId === null) return null;
  return String(accountId).trim() || null;
};
const captureAccountContext = () => ({
  accountId: normalizedAccountId(),
  generation: accountGeneration,
});
const accountContextIsCurrent = context =>
  context.accountId !== null &&
  context.accountId === normalizedAccountId() &&
  context.generation === accountGeneration;

const saving = ref(false);
const labelMappingsValid = ref(true);
const autoAssignmentValid = ref(true);
const labelMappingsData = ref(null);
const discardConfirmation = ref(null);

// Fase 4.4 + 4.5 — local refs for the new settings sections
const autoAssignmentDraft = ref(null);
const requireLossReasonDraft = ref(null);

// Get selected pipeline from store
const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);

const deeplyEqual = (firstValue, secondValue) => {
  if (Object.is(firstValue, secondValue)) return true;
  if (Array.isArray(firstValue) || Array.isArray(secondValue)) {
    return (
      Array.isArray(firstValue) &&
      Array.isArray(secondValue) &&
      firstValue.length === secondValue.length &&
      firstValue.every((value, index) => deeplyEqual(value, secondValue[index]))
    );
  }
  if (
    !firstValue ||
    !secondValue ||
    typeof firstValue !== 'object' ||
    typeof secondValue !== 'object'
  ) {
    return false;
  }

  const firstKeys = Object.keys(firstValue).sort();
  const secondKeys = Object.keys(secondValue).sort();
  return (
    deeplyEqual(firstKeys, secondKeys) &&
    firstKeys.every(key => deeplyEqual(firstValue[key], secondValue[key]))
  );
};

const normalizeLabelMappings = value => ({
  label_stage_mappings: Array.isArray(value?.label_stage_mappings)
    ? value.label_stage_mappings
    : [],
  stage_label_mappings: Array.isArray(value?.stage_label_mappings)
    ? value.stage_label_mappings
    : [],
});

const normalizeAutoAssignment = value => ({
  enabled: value?.enabled === true,
  strategy: value?.strategy || 'round_robin',
  agent_ids: Array.isArray(value?.agent_ids)
    ? value.agent_ids.map(Number).filter(Boolean)
    : [],
  max_per_agent:
    value?.max_per_agent === undefined ||
    value?.max_per_agent === null ||
    value?.max_per_agent === ''
      ? null
      : Number(value.max_per_agent),
});

const resetDrafts = () => {
  labelMappingsValid.value = true;
  autoAssignmentValid.value = true;
  labelMappingsData.value = null;
  autoAssignmentDraft.value = null;
  requireLossReasonDraft.value = null;
};

const onAutoAssignmentChange = data => {
  if (!isAdmin.value) return;
  const currentValue = selectedPipeline.value?.settings?.auto_assignment;
  autoAssignmentDraft.value = deeplyEqual(
    normalizeAutoAssignment(data),
    normalizeAutoAssignment(currentValue)
  )
    ? null
    : data;
};
const onLossReasonChange = data => {
  if (!isAdmin.value) return;
  const currentValue =
    selectedPipeline.value?.settings?.require_loss_reason === true;
  requireLossReasonDraft.value =
    data?.require_loss_reason === currentValue ? null : data;
};

// Convert stages object to array for LabelStageMappings
const stagesArray = computed(() => {
  const stagesObj = selectedPipeline.value?.stages || {};
  return Object.entries(stagesObj)
    .map(([id, stage]) => ({
      id,
      name: stage.name,
      color: stage.color,
      position: parseInt(stage.position, 10) || 0,
      is_won_stage: stage.is_won_stage || false,
      is_lost_stage: stage.is_lost_stage || false,
    }))
    .sort((a, b) => a.position - b.position);
});

const hasDraftChanges = computed(
  () =>
    isAdmin.value &&
    (labelMappingsData.value !== null ||
      autoAssignmentDraft.value !== null ||
      requireLossReasonDraft.value !== null)
);

const closeSettings = () => {
  resetDrafts();
  emit('close');
};

const handleClose = async () => {
  if (saving.value) return;
  if (!isAdmin.value || !hasDraftChanges.value) {
    closeSettings();
    return;
  }

  const confirmed = await discardConfirmation.value.showConfirmation();
  if (confirmed) closeSettings();
};

watch(
  [() => props.show, () => selectedPipeline.value?.id],
  ([show], [previousShow, previousPipelineId]) => {
    if (
      !show ||
      !previousShow ||
      previousPipelineId !== selectedPipeline.value?.id
    ) {
      resetDrafts();
    }
  }
);

const onLabelMappingsUpdate = mappings => {
  if (!isAdmin.value) return;
  const currentMappings = normalizeLabelMappings(
    selectedPipeline.value?.settings
  );
  const normalizedMappings = normalizeLabelMappings(mappings);
  labelMappingsData.value = deeplyEqual(normalizedMappings, currentMappings)
    ? null
    : normalizedMappings;
};

const onLabelMappingsValidUpdate = valid => {
  labelMappingsValid.value = valid;
};

const handleSave = async () => {
  if (saving.value) return;
  if (!isAdmin.value || !selectedPipeline.value?.id) {
    return;
  }

  const hasLabelChanges = !!labelMappingsData.value;
  const hasAutoAssignChanges = autoAssignmentDraft.value !== null;
  const hasLossReasonChanges = requireLossReasonDraft.value !== null;

  if (!hasLabelChanges && !hasAutoAssignChanges && !hasLossReasonChanges) {
    closeSettings();
    return;
  }
  const accountContext = captureAccountContext();
  if (accountContext.accountId === null) return;
  saveGeneration += 1;
  const requestGeneration = saveGeneration;
  const requestIsCurrent = () =>
    requestGeneration === saveGeneration &&
    accountContextIsCurrent(accountContext);

  saving.value = true;
  try {
    const currentSettings = selectedPipeline.value.settings || {};
    const newSettings = { ...currentSettings };

    if (hasLabelChanges) {
      newSettings.label_stage_mappings =
        labelMappingsData.value.label_stage_mappings || [];
      newSettings.stage_label_mappings =
        labelMappingsData.value.stage_label_mappings || [];
    }
    if (hasAutoAssignChanges) {
      newSettings.auto_assignment = autoAssignmentDraft.value;
    }
    if (hasLossReasonChanges) {
      newSettings.require_loss_reason =
        requireLossReasonDraft.value.require_loss_reason;
    }

    await store.dispatch('pipelines/updatePipeline', {
      id: selectedPipeline.value.id,
      accountId: accountContext.accountId,
      settings: newSettings,
    });
    if (!requestIsCurrent()) return;
    showAlert(t('PIPELINE.SETTINGS.SAVE_SUCCESS'));
    closeSettings();
  } catch (error) {
    if (!requestIsCurrent()) return;
    logger.error('Failed to save pipeline settings:', error);
    showAlert(t('PIPELINE.SETTINGS.SAVE_ERROR'));
  } finally {
    if (requestIsCurrent()) saving.value = false;
  }
};

watch(
  currentAccountId,
  (accountId, previousAccountId) => {
    if (String(accountId ?? '') === String(previousAccountId ?? '')) return;
    accountGeneration += 1;
    saveGeneration += 1;
    saving.value = false;
    resetDrafts();
  },
  { flush: 'sync' }
);
</script>

<template>
  <Modal
    :show="show"
    size="medium"
    aria-labelledby="pipeline-settings-title"
    @close="handleClose"
  >
    <div class="flex flex-col">
      <header class="p-4 border-b border-n-weak">
        <h3 id="pipeline-settings-title" class="text-heading-3 text-n-slate-12">
          {{ t('PIPELINE.SETTINGS.TITLE') }}
        </h3>
      </header>

      <div class="p-4 space-y-6 max-h-[70dvh] overflow-y-auto">
        <!-- Non-admin read-only notice -->
        <div
          v-if="!isAdmin"
          class="rounded-lg bg-n-amber-3 border border-n-amber-9 px-4 py-3 text-sm text-n-amber-11"
        >
          {{ t('PIPELINE.SETTINGS.ADMIN_ONLY_NOTICE') }}
        </div>

        <!-- Divider -->
        <div
          v-if="!selectedPipeline"
          role="status"
          class="rounded-lg border border-n-weak bg-n-alpha-1 p-4 text-center text-sm text-n-slate-11"
        >
          {{ t('PIPELINE.SETTINGS.NO_PIPELINE') }}
        </div>

        <hr v-if="selectedPipeline" class="border-n-weak" />

        <!-- Label Automations -->
        <div v-if="selectedPipeline">
          <LabelStageMappings
            :pipeline="selectedPipeline"
            :stages="stagesArray"
            :readonly="!isAdmin || saving"
            @update:mappings="onLabelMappingsUpdate"
            @update:valid="onLabelMappingsValidUpdate"
          />
        </div>

        <!-- Fase 4.4 — Auto-assignment -->
        <hr v-if="selectedPipeline" class="border-n-weak" />
        <PipelineAutoAssignmentSetting
          v-if="selectedPipeline"
          :pipeline="selectedPipeline"
          :readonly="!isAdmin || saving"
          @change="onAutoAssignmentChange"
          @valid="autoAssignmentValid = $event"
        />

        <!-- Fase 4.5 — Required loss reason -->
        <hr v-if="selectedPipeline" class="border-n-weak" />
        <PipelineLossReasonSetting
          v-if="selectedPipeline"
          :pipeline="selectedPipeline"
          :readonly="!isAdmin || saving"
          @change="onLossReasonChange"
        />
      </div>

      <footer
        class="flex flex-col-reverse items-stretch gap-2 border-t border-n-weak p-4 sm:flex-row sm:justify-end"
      >
        <Button
          type="button"
          variant="ghost"
          color="slate"
          size="md"
          :disabled="saving"
          @click="handleClose"
        >
          {{ t('PIPELINE.CANCEL') }}
        </Button>
        <Button
          v-if="isAdmin"
          type="button"
          variant="solid"
          color="blue"
          size="md"
          :is-loading="saving"
          :disabled="
            !selectedPipeline ||
            !hasDraftChanges ||
            !labelMappingsValid ||
            !autoAssignmentValid ||
            saving
          "
          @click="handleSave"
        >
          {{ t('PIPELINE.SAVE') }}
        </Button>
      </footer>
    </div>
  </Modal>

  <ConfirmModal
    ref="discardConfirmation"
    :title="t('PIPELINE.SETTINGS.DISCARD_TITLE')"
    :description="t('PIPELINE.SETTINGS.DISCARD_DESCRIPTION')"
    :confirm-label="t('PIPELINE.SETTINGS.DISCARD_CONFIRM')"
    :cancel-label="t('PIPELINE.SETTINGS.KEEP_EDITING')"
  />
</template>
